<template lang="pug">
md-layout(md-column='')
  // Page loader
  md-progress.md-accent(md-indeterminate='', v-show='loadingComponent')
  // Page content
  md-theme(md-name='blue', v-show='!loadingComponent')
    // Title
    breadcrumbs(section='Users', :nav='nav')
    stitch-loader(:loading='loading', :size='40')
      // Form
      md-layout.form-wrapper(:md-gutter='16')
        md-whiteframe.form-wrapper-frame(md-elevation='3')
          account-selector(v-model='user.user_metadata.account_id', :showRequiredError='formSubmitted && $v.user.user_metadata.account_id.$invalid')

          md-layout(:md-flex='100', md-align='center')
            span.on-error(v-if="errorMsg") {{ errorMsg }}

          md-input-container(:class='{ "md-input-invalid" : formSubmitted && $v.user.user_metadata.name.$invalid }')
            label Name
            md-input(v-model='user.user_metadata.name')
            span.md-error(v-if='formSubmitted && !$v.user.user_metadata.name.required') This field is required

          md-input-container(:class='{ "md-input-invalid" : formSubmitted && $v.user.email.$invalid }')
            label Email
            md-input(v-model='user.email')
            span.md-error(v-if='formSubmitted && !$v.user.email.required') This field is required

          md-input-container(:class='{ "md-input-invalid" : formSubmitted && $v.user.password.$invalid }')
            label Password
            md-input(v-model='user.password', type="password")
            span.md-error(v-if='formSubmitted && !$v.user.password.required') This field is required

          md-input-container(v-if="!siteuser.authorization.roles.includes('User')" :class='{ "md-input-invalid" : formSubmitted && $v.user.app_metadata.authorization.roles.$invalid }')
            user-roles-selector.inputRoles(v-model='user.app_metadata.authorization.roles')
            span.md-error(v-if='formSubmitted && !$v.user.app_metadata.authorization.roles.required') This field is required

      md-layout(md-align='center')
        stitch-loader(:loading='creatingOrUpdating', :size='40')
        md-button#submit-btn.md-raised.md-primary(@click='onFormSubmit', v-show='!creatingOrUpdating', :disabled="$v.$pending", md-theme='blue')
          | {{ editMode ? 'Update' : 'Create' }}
</template>

<script>
  import { mapState, mapGetters } from 'vuex';
  import { validationMixin } from 'vuelidate';
  import { required, email, maxLength } from 'vuelidate/lib/validators';
  import StitchLoader from '../../shared-components/Loader';
  import accountService from '../../services/accountService';
  import userService from '../../services/userService';
  import AccountSelector from '../../shared-components/account/AccountSelector';
  import UserRolesSelector from '../../shared-components/user/UserRolesSelector';
  import Breadcrumbs from '../../shared-components/Breadcrumbs';

  export default {
    mixins: [validationMixin],
    components: {
      StitchLoader,
      AccountSelector,
      UserRolesSelector,
      Breadcrumbs
    },
    data() {
      return {
        errorMsg: '',
        userOriginalName: null,
        userOriginalEmail: null,
        loadingComponent: false,
        creatingOrUpdating: false,
        loading: false,
        formSubmitted: false,
        user: {
          email: '',
          password: '',
          user_metadata: {
            account_id: '',
            name: ''
          },
          app_metadata: {
            authorization: {
              groups: [],
              roles: [],
              permissions: []
            }
          }
        }
      };
    },
    validations: {
      user: {
        email: {
          email,
          required
        },
        password: {
          required(value) {
            return this.editMode || value.length;
          }
        },
        app_metadata: {
          authorization: {
            roles: {
              required
            }
          }
        },
        user_metadata: {
          account_id: {
            required
          },
          name: {
            required
          }
        }
      }
    },
    computed: {
      ...mapState({
        siteuser: state => state.user.siteuser
      }),
      nav() {
        return [
          { title: 'Users', route: 'UserList' },
          { title: this.editMode ? 'Edit User' : 'New User' }
        ]
      },
      editMode() {
        return !!this.$route.params.id;
      }
    },
    methods: {
      showAlertToast(text, icon){
        this.$swal({
              text: text,
              position: 'bottom-left',
              icon: icon,
              toast: true,
              timer: 3000,
              timerProgressBar: true,
              showConfirmButton: false,
              }
            )
      },

      isAdmin() {
        return this.siteuser.authorization.roles.includes('Admin');
      },
      canImpersonate() {
        // only show impersonate on Admins own profile
        return this.isAdmin() && this.siteuser.user_id === decodeURI(this.$route.params.id);
      },
      getUser(data) {
        return {
          email: data.email || '',
          user_metadata: data.user_metadata || {
            account_id: '',
            name: '',
          },
          app_metadata: data.app_metadata || {
            authorization: {
              groups: [],
              roles: [],
              permissions: []
            }
          }
        };
      },
      sanitizeUserData(user) {
        const {
          password,
          user_metadata,
          app_metadata
        } = user;
        const sanitizedUser = {
          password,
          user_metadata,
          app_metadata
        };
        if (user.email !== this.userOriginalEmail) {
          sanitizedUser.email = user.email;
        }
        return sanitizedUser;
      },
      createUser() {
        this.creatingOrUpdating = true;
        userService.create(this.sanitizeUserData(this.user))
          .then(() => {
            this.showAlertToast('User created successfully', 'success');
            this.$delayedNavigation(this.$router, 'UserList');
          })
          .catch((error) => {
            this.errorMsg = error.response.data.errors.error.message;
          })
          .then(() => {
            this.creatingOrUpdating = false;
          });
      },
      updateUser( reLogin = false ) {
        this.creatingOrUpdating = true;
        this.errorMsg = '';
        userService.update(this.$route.params.id, this.sanitizeUserData(this.user))
          .then(() => {
            this.userOriginalEmail = this.user.email;
            this.showAlertToast('User updated successfully', 'success');
            this.$delayedNavigation(this.$router, reLogin ? 'Logout' : 'UserList');
          })
          .catch((error) => {
            this.errorMsg = error.response.data.errors.error.message;
          })
          .then(() => {
            this.creatingOrUpdating = false;
          });
      },

      confirmDecision(){
        this.$swal.fire({
          text:'You are about to downgrade your user level. This action can only be undone by someone with a higher user level. Do you want to proceed?. This action will require re-login',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText:'No',
          }).then(res => {
            if(res.isConfirmed){
              this.updateUser(true)
            }
          })
      },

      onFormSubmit() {
        this.formSubmitted = true;

        if (!this.$v.$invalid) {
          if (this.editMode) {
          // check if the same user and role is downgrade 
            (this.user.app_metadata.authorization.roles[0] !== this.siteuser.authorization.roles[0]) 
            && (this.siteuser.user_id.slice(6) === this.$route.params.id.slice(8))
              ? this.confirmDecision()
              : this.updateUser();

          } else {
            this.createUser();
          }
        }
      }
    },
    mounted() {
      if (this.editMode) {
        this.loading = true;
        this.loadingComponent = true;
        userService.get(this.$route.params.id)
          .then((response) => {
            this.user = this.getUser(response.data.user);
            this.userOriginalEmail = this.user.email;

          })
          .catch(() => {
            this.showAlertToast('User data could not be retrieved. Please, try again later.', 'error');
          })
          .then(() => {
            this.loadingComponent = false;
            this.loading = false;
          });
      } else {
        this.user.user_metadata.account_id = this.siteuser.user_metadata.account_id;

        if(this.isAdmin() && localStorage.getItem('impersonate')) {
          this.user.user_metadata.account_id = localStorage.getItem('impersonate');
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .form-wrapper {
    width: 100%;
    padding: 25px 25%;
    .form-wrapper-frame {
      width: 100%;
      padding: 25px;
      margin-bottom: 15px;
      .md-subheading {
        margin-top: 35px;
      }
    }
    @media (max-width: 1200px) {
      padding: 10px .5%;
      margin-left: 0;
    }
  }

  .inputRoles{
    width: 100%;
  }

  .on-error {
    color: #ff5722;
    margin-bottom: 15px;
  }
  .title-icon {
    margin-top: -5px;
  }
  #submit-btn {
    margin: 25px 0;
    width: 33%;
    padding: 6px;
    font-size: 1.1em;
  }
</style>



// WEBPACK FOOTER //
// src/spa/user/UserForm.vue